/**
 * Credit Note Service
 * Handles credit note creation, application, and management
 */

/**
 * Generate a new credit note number
 * @param {Array} existingNumbers - Array of existing credit note numbers
 * @param {number} year - Year for the credit note
 * @returns {string} New credit note number
 */
export const generateCreditNoteNumber = (existingNumbers, year) => {
  const prefix = `CN-${year}-`;

  // Filter numbers for the current year
  const currentYearNumbers = existingNumbers.filter((num) =>
    num.startsWith(prefix)
  );

  if (currentYearNumbers.length === 0) {
    return `${prefix}0001`;
  }

  // Extract sequence numbers and find max
  const sequences = currentYearNumbers.map((num) => {
    const match = num.match(/CN-\d{4}-(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  });

  const maxSequence = Math.max(...sequences);
  const nextSequence = maxSequence + 1;

  return `${prefix}${String(nextSequence).padStart(4, '0')}`;
};

/**
 * Validate credit note amount
 * @param {number} amount - Credit amount
 * @param {Object|null} invoice - Invoice object (null for standalone credit)
 * @returns {Object} Validation result
 */
export const validateCreditNoteAmount = (amount, invoice) => {
  if (amount <= 0) {
    return {
      valid: false,
      error: 'Credit note amount must be a positive number',
    };
  }

  if (invoice && amount > invoice.totalAmount) {
    return {
      valid: false,
      error: `Credit note amount cannot exceed invoice total (${invoice.totalAmount})`,
    };
  }

  return { valid: true };
};

/**
 * Calculate credit application to an invoice
 * @param {Object} invoice - Invoice object
 * @param {number} creditAmount - Amount of credit to apply
 * @returns {Object} Application calculation result
 */
export const calculateCreditApplication = (invoice, creditAmount) => {
  const amountPaid = invoice.amountPaid || 0;
  const remainingBalance = invoice.totalAmount - amountPaid;

  // Cap credit at remaining balance
  const amountToApply = Math.min(creditAmount, remainingBalance);
  const newBalance = remainingBalance - amountToApply;
  const remainingCredit = creditAmount - amountToApply;

  return {
    previousBalance: remainingBalance,
    amountApplied: amountToApply,
    newBalance,
    remainingCredit,
    fullyPaid: newBalance === 0,
  };
};

/**
 * Create credit note data object
 * @param {Object} input - Credit note input data
 * @param {string} creditNoteNumber - Generated credit note number
 * @returns {Object} Credit note data
 */
export const createCreditNoteData = (input, creditNoteNumber) => {
  return {
    creditNoteNumber,
    clientId: input.clientId,
    clientName: input.clientName,
    userId: input.userId,
    invoiceId: input.invoiceId || null,
    amount: input.amount,
    currency: input.currency,
    reason: input.reason,
    lineItems: input.lineItems || [
      {
        description: input.reason || 'Credit adjustment',
        quantity: 1,
        unitPrice: input.amount,
        amount: input.amount,
      },
    ],
    status: 'draft',
    appliedTo: [],
    refundDetails: null,
    issuedAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
};

/**
 * Apply credit note to an invoice
 * @param {Object} creditNote - Credit note object
 * @param {Object} invoice - Invoice object
 * @param {number} amountToApply - Amount to apply
 * @returns {Object} Updated credit note and invoice
 */
export const applyCreditToInvoice = (creditNote, invoice, amountToApply) => {
  const calculation = calculateCreditApplication(invoice, amountToApply);

  // Update invoice
  const updatedInvoice = {
    ...invoice,
    amountPaid: (invoice.amountPaid || 0) + calculation.amountApplied,
    status: calculation.fullyPaid ? 'paid' : invoice.status,
    updatedAt: new Date(),
  };

  if (calculation.fullyPaid) {
    updatedInvoice.paidAt = new Date();
  }

  // Calculate total applied from this credit note
  const previouslyApplied = (creditNote.appliedTo || []).reduce(
    (sum, app) => sum + app.amount,
    0
  );
  const totalApplied = previouslyApplied + calculation.amountApplied;

  // Update credit note
  const updatedCreditNote = {
    ...creditNote,
    appliedTo: [
      ...(creditNote.appliedTo || []),
      {
        invoiceId: invoice.id,
        amount: calculation.amountApplied,
        appliedAt: new Date(),
      },
    ],
    status: totalApplied >= creditNote.amount ? 'applied' : 'issued',
    updatedAt: new Date(),
  };

  return {
    updatedInvoice,
    updatedCreditNote,
    amountApplied: calculation.amountApplied,
    remainingCredit: creditNote.amount - totalApplied,
    invoiceFullyPaid: calculation.fullyPaid,
  };
};

/**
 * Get available credit from a credit note
 * @param {Object} creditNote - Credit note object
 * @returns {number} Available credit amount
 */
export const getAvailableCredit = (creditNote) => {
  // Only issued credit notes have available credit
  if (creditNote.status !== 'issued') {
    return 0;
  }

  const appliedTotal = (creditNote.appliedTo || []).reduce(
    (sum, app) => sum + app.amount,
    0
  );

  return Math.max(0, creditNote.amount - appliedTotal);
};

/**
 * Check if credit note can be voided
 * @param {Object} creditNote - Credit note object
 * @returns {Object} Result with canVoid and reason
 */
export const canVoidCreditNote = (creditNote) => {
  if (creditNote.status === 'void') {
    return { canVoid: false, reason: 'Credit note is already voided' };
  }

  if (creditNote.status === 'applied') {
    return { canVoid: false, reason: 'Cannot void a fully applied credit note' };
  }

  if (creditNote.status === 'refunded') {
    return { canVoid: false, reason: 'Cannot void a refunded credit note' };
  }

  if ((creditNote.appliedTo || []).length > 0) {
    return { canVoid: false, reason: 'Cannot void credit note with partial applications' };
  }

  return { canVoid: true };
};

/**
 * Get credit note summary for a client
 * @param {Array} creditNotes - Array of credit notes
 * @param {string} clientId - Client ID
 * @returns {Object} Credit summary
 */
export const getClientCreditSummary = (creditNotes, clientId) => {
  const clientNotes = creditNotes.filter((cn) => cn.clientId === clientId);

  const summary = {
    totalIssued: 0,
    totalApplied: 0,
    totalAvailable: 0,
    noteCount: clientNotes.length,
    activeNotes: [],
  };

  clientNotes.forEach((note) => {
    summary.totalIssued += note.amount;

    const applied = (note.appliedTo || []).reduce(
      (sum, app) => sum + app.amount,
      0
    );
    summary.totalApplied += applied;

    const available = getAvailableCredit(note);
    summary.totalAvailable += available;

    if (available > 0) {
      summary.activeNotes.push({
        id: note.id,
        number: note.creditNoteNumber,
        available,
      });
    }
  });

  return summary;
};
